import { createContext, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createAuthProvider } from 'react-token-auth';
import { getSessionUUID } from '../components/lib/AuthToken';
import logger from "../utils/logger";
import { check_customer_warehouse_access } from '../api/route';

let isRefreshing = false;

// Create auth provider using react-token-auth
const { useAuth, authFetch: _authFetch, login, logout } = createAuthProvider({
    getAccessToken: (session) => session.session_uuid,
    storage: localStorage,
    onUpdateToken: (token) => {
        const session_uuid = token?.session_uuid;

        if (!session_uuid) {
            logout();
            return Promise.reject('No session token available');
        }

        if (isRefreshing) {
            return Promise.reject('Refresh token request already in progress');
        }

        isRefreshing = true;

        return fetch('/auth/refresh', {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ sessionToken: session_uuid }),
        })
            .then((r) => {
                if (!r.ok) {
                    if (r.status === 429) {
                        // If refresh is in progress, wait and retry
                        throw new Error('REFRESH_IN_PROGRESS');
                    }
                    if (r.status === 503) {
                        // Network connectivity issue - don't logout, just fail refresh
                        throw new Error('NETWORK_ERROR');
                    }
                    throw new Error(`Failed to refresh token: ${r.status}`);
                }
                return r.json();
            })
            .then((data) => {
                if (data.session_uuid) {
                    login({ session_uuid: data.session_uuid });
                } else if (data.reason === 'date_changed') {
                    // Calendar day has rolled over — force a clean re-login
                    logout();
                    window.location.href = '/';
                } else {
                    throw new Error('Invalid tokens received');
                }
            })
            .catch(async (error) => {
                logger.error('Error refreshing token:', error);
                
                // Handle network connectivity issues - don't logout, just retry later
                if (error.message.includes('NETWORK_ERROR') || error.message.includes('503')) {
                    logger.warn('Network connectivity issue during token refresh');
                    return Promise.reject('Network connectivity issue - please try again');
                }
                
                // Handle specific "Already Used" errors and 429 (refresh in progress) with retry
                if (error.message.includes('Already Used') || 
                    error.message.includes('REFRESH_IN_PROGRESS') ||
                    error.message.includes('429')) {
                    logger.debug('Handling token conflict, waiting and retrying');
                    
                    // Wait longer for backend to resolve conflicts
                    await new Promise(resolve => setTimeout(resolve, 3000));
                    
                    try {
                        const retryResponse = await fetch('/auth/refresh', {
                            method: 'POST',
                            credentials: 'include',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ sessionToken: session_uuid }),
                        });
                        
                        if (retryResponse.ok) {
                            const retryData = await retryResponse.json();
                            if (retryData.session_uuid) {
                                logger.info('Retry successful, token refreshed');
                                login({ session_uuid: retryData.session_uuid });
                                return;
                            }
                        } else if (retryResponse.status === 429) {
                            // Still in progress, give up gracefully
                            logger.warn('Refresh still in progress after retry');
                            return Promise.reject('Refresh timeout - please try again');
                        } else if (retryResponse.status === 503) {
                            // Network issue on retry too
                            logger.warn('Network connectivity issue on retry');
                            return Promise.reject('Network connectivity issue - please try again');
                        } else {
                            logger.error('Retry failed', { status: retryResponse.status });
                        }
                    } catch (retryError) {
                        logger.error('Retry failed:', retryError);
                    }
                }
                
                // If we get here, refresh failed completely
                // Don't logout for network errors - only for auth errors
                if (!error.message.includes('Network connectivity issue') && 
                    !error.message.includes('NETWORK_ERROR')) {
                    logger.error('Token refresh failed completely, logging out');
                    logout();
                } else {
                    logger.warn('Network issue during refresh - keeping user logged in');
                }
                return Promise.reject(error);
            })
            .finally(() => {
                isRefreshing = false;
            });
    },
});

// Wrap authFetch to globally handle totp_grace_expired 401s.
// When the 6-hour TOTP grace window expires mid-session, any API call returns
// { error: '2FA session expired', reason: 'totp_grace_expired' }.
// We catch it here, clear local auth state and redirect to login so the user
// is challenged for 2FA again without needing per-call handling.
export const authFetch = async (...args) => {
    const response = await _authFetch(...args);
    if (response.status === 401) {
        // Clone so the original can still be consumed by the caller if needed
        const cloned = response.clone();
        try {
            const data = await cloned.json();
            if (data.reason === 'totp_grace_expired') {
                logger.warn('TOTP grace period expired — redirecting to login');
                logout();
                window.location.href = '/';
                // Return a never-resolving promise so the caller doesn't process stale data
                return new Promise(() => {});
            }
        } catch (_) { /* not JSON — fall through */ }
    }
    return response;
};
export { useAuth, login, logout };

// Global fetch interceptor — catches totp_grace_expired from plain fetch() calls
// (the majority of API files use fetch directly rather than authFetch).
// Installed once here so every fetch in the app is covered without per-file changes.
const _originalFetch = window.fetch.bind(window);
window.fetch = async (...args) => {
    const response = await _originalFetch(...args);
    if (response.status === 401) {
        const cloned = response.clone();
        try {
            const data = await cloned.json();
            if (data.reason === 'totp_grace_expired') {
                logger.warn('TOTP grace period expired (fetch interceptor) — redirecting to login');
                logout();
                window.location.href = '/';
                return new Promise(() => {});
            }
        } catch (_) { /* not JSON — fall through */ }
    }
    return response;
};

// Create a context for the auth provider
const AuthContext = createContext(null);

// Create a custom provider component
export const AuthProvider = ({ children }) => {

    const defaultFeatures = {
        kpis: false,
        warehouse: false,
        alcohol_testing: false,
        tacho: false,
        timesheets: false,
        holidays: false,
        collections: false,
        purchase_orders: false,
        ai: false,
        fire_register: false,
        messaging: false,
        mobile: false,
        uptime_robot: false,
        service: false,
        routing: false,
        invoicing: false
    }

    const navigate = useNavigate();
    let [loggedIn] = useAuth();
    const [profile, setProfile] = useState(null);
    const [isLoading, setLoading] = useState(false);
    const [isAdmin, setIsAdmin] = useState(false);
    const [isManager, setIsManager] = useState(false);
    const [isDev, setIsDev] = useState(false);
    const [isWarehouse, setIsWarehouse] = useState(false);
    const [isCustomer, setIsCustomer] = useState(false);
    const [isService, setIsService] = useState(false);
    const [hasCustomerWarehouseAccess, setHasCustomerWarehouseAccess] = useState(false);
    const [session, setSession] = useState(getSessionUUID());
    const [features, setFeatures] = useState(defaultFeatures); // null while loading, object once fetched
    const [companyLogo, setCompanyLogo] = useState(null);

    // Fetch user profile
    const fetchProfile = async () => {
        try {
            const response = await authFetch('/profile/get_profile');
            if (!response.ok) {
                throw new Error('Failed to fetch profile');
            }
            const data = await response.json();
            await setProfile(data);
            await setIsAdmin(data.group === 'ADMIN');
            setIsManager(data.is_super_admin);
            setIsDev(data.is_dev);
            setIsWarehouse(data.group === 'WAREHOUSE');
            setIsCustomer(data.group === 'CUSTOMER');
            setIsService(data.group === 'SERVICE');

            // Check warehouse access for customer accounts
            if (data.group === 'CUSTOMER') {
                try {
                    const warehouseCheck = await check_customer_warehouse_access();
                    setHasCustomerWarehouseAccess(warehouseCheck.has_access === true);
                } catch {
                    setHasCustomerWarehouseAccess(false);
                }
            }

            // Fetch feature flags (fail-open: all enabled if request fails)
            try {
                const featuresResp = await authFetch('/dev/get_features');
                if (featuresResp.ok) {
                    const featuresData = await featuresResp.json();
                    setFeatures(featuresData);
                    setCompanyLogo(featuresData.logo_url || null);
                } else {
                    setFeatures(defaultFeatures);
                }
            } catch {
                setFeatures(defaultFeatures);
            }

        } catch (error) {
            logger.error('Error fetching profile:', error);
            handleLogout();
            navigate('/');

        } finally {
            // pause for 1 second to allow states to update
            setTimeout(() => {
                setLoading(false);
            }, 2000);
        }
    };


    // Automatically fetch profile if logged in
    useEffect(() => {
        if (!loggedIn || profile) {
            return;
        }
        setLoading(true);
        fetchProfile();
    },[loggedIn, profile]);

    // Note: Token refresh is handled automatically by react-token-auth
    // Removed periodic refresh interval to prevent race conditions

    // logout and clear session data
    const handleLogout = () => {

        setIsAdmin(false);
        setIsManager(false);
        setIsDev(false);
        setIsWarehouse(false);
        setIsCustomer(false);
        setIsService(false);
        setHasCustomerWarehouseAccess(false);
        setProfile(null);
        setFeatures(defaultFeatures);
        setCompanyLogo(null);
        logout();
    }
    
    //========================================================
    //========================================================
    // Customer status tracking removed for production
    //========================================================
    //========================================================

    // Provide necessary data and methods
    const permissions = profile || {};

    const value = {
        login,
        logout,
        session,
        loggedIn,
        profile,
        permissions,
        isLoading,
        isAdmin,
        isManager,
        isDev,
        isWarehouse,
        isCustomer,
        isService,
        hasCustomerWarehouseAccess,
        handleLogout,
        refreshProfile: fetchProfile,
        features,
        companyLogo,
        setCompanyLogo,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Create a custom hook for accessing the Auth context
export const useAuthProvider = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuthProvider must be used within an AuthProvider');
    }
    return context;
};
